import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function Availability() {
  const [blockedDays, setBlockedDays] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [blockStart, setBlockStart] = useState('');
  const [blockEnd, setBlockEnd] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const load = async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const docSnap = await getDoc(doc(db, 'workerAvailability', user.uid));
        if (docSnap.exists()) {
          setBlockedDays(docSnap.data().blockedDays || []);
          setBlockedDates(docSnap.data().blockedDates || []);
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, []);

  const toggleBlockedDay = (day) => {
    setBlockedDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const addBlockedRange = () => {
    if (!blockStart || !blockEnd) return;
    const start = new Date(blockStart + 'T00:00:00');
    const end = new Date(blockEnd + 'T00:00:00');
    if (end < start) return;
    const newDates = [];
    const current = new Date(start);
    while (current <= end) {
      const dateStr = current.toISOString().split('T')[0];
      if (!blockedDates.find(b => b.date === dateStr)) {
        newDates.push({ date: dateStr, reason: blockReason || 'Unavailable' });
      }
      current.setDate(current.getDate() + 1);
    }
    setBlockedDates(prev => [...prev, ...newDates]);
    setBlockStart('');
    setBlockEnd('');
    setBlockReason('');
  };

  const removeBlockedRange = (reason) => {
    setBlockedDates(prev => prev.filter(b => b.reason !== reason));
  };

  const isDateBlocked = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const date = new Date(dateStr + 'T00:00:00');
    const dayOfWeek = date.getDay();
    if (blockedDays.includes(DAYS_OF_WEEK[dayOfWeek])) return 'day';
    if (blockedDates.find(b => b.date === dateStr)) return 'date';
    return false;
  };

  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  };

  const handleSave = async () => {
    const user = auth.currentUser;
    setSaving(true);
    try {
      await setDoc(doc(db, 'workerAvailability', user.uid), {
        blockedDays, blockedDates, updatedAt: new Date(), userId: user.uid,
      });
      await setDoc(doc(db, 'workerProfiles', user.uid), {
        blockedDays, blockedDates,
      }, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const groupedRanges = blockedDates.reduce((groups, item) => {
    const key = item.reason || 'Unavailable';
    if (!groups[key]) groups[key] = [];
    groups[key].push(item.date);
    return groups;
  }, {});

  if (loading) {
    return <div style={styles.loadingContainer}><p>Loading...</p></div>;
  }

  const calendarSection = (
    <div style={styles.calendarCard}>
      <div style={styles.calendarHeader}>
        <button style={styles.navBtn} onClick={() => setCurrentDate(new Date(year, month - 1, 1))}>←</button>
        <h3 style={styles.monthTitle}>{monthNames[month]} {year}</h3>
        <button style={styles.navBtn} onClick={() => setCurrentDate(new Date(year, month + 1, 1))}>→</button>
      </div>
      <div style={styles.dayNamesRow}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} style={styles.dayName}>{d}</div>
        ))}
      </div>
      <div style={styles.daysGrid}>
        {Array.from({ length: firstDay }).map((_, i) => (
          <div key={`e-${i}`} style={styles.emptyCell} />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const blocked = isDateBlocked(day);
          const todayDay = isToday(day);
          return (
            <div key={day} style={{
              ...styles.dayCell,
              ...(todayDay ? styles.todayCell : {}),
              ...(blocked === 'day' ? styles.blockedDayCell : {}),
              ...(blocked === 'date' ? styles.blockedDateCell : {}),
            }}>
              <span style={styles.dayNumber}>{day}</span>
              {blocked && <span style={styles.blockedDot}>×</span>}
            </div>
          );
        })}
      </div>
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendBox, backgroundColor: '#ffebee' }} />
          <span>Recurring</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendBox, backgroundColor: '#fce4ec' }} />
          <span>Blocked Date</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendBox, backgroundColor: '#1a1a2e' }} />
          <span>Today</span>
        </div>
      </div>
    </div>
  );

  const controlsSection = (
    <div style={styles.controls}>
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>🔁 Block Recurring Days</h3>
        <p style={styles.hint}>Select days of the week you never work</p>
        <div style={styles.dayGrid}>
          {DAYS_OF_WEEK.map(day => (
            <button key={day} type="button"
              style={blockedDays.includes(day) ? styles.dayBtnBlocked : styles.dayBtn}
              onClick={() => toggleBlockedDay(day)}>
              {day.slice(0, 3)}
            </button>
          ))}
        </div>
        {blockedDays.length > 0 && (
          <p style={styles.blockedSummary}>🚫 Blocking all {blockedDays.join(', ')}s</p>
        )}
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>📅 Block Date Range</h3>
        <p style={styles.hint}>Block specific dates like vacation or personal time</p>
        <label style={styles.fieldLabel}>Start Date</label>
        <input style={styles.input} type="date" value={blockStart}
          onChange={e => {
            setBlockStart(e.target.value);
            if (!blockEnd || blockEnd < e.target.value) setBlockEnd(e.target.value);
          }}
          min={new Date().toISOString().split('T')[0]} />
        <label style={styles.fieldLabel}>End Date</label>
        <input style={styles.input} type="date" value={blockEnd}
          onChange={e => setBlockEnd(e.target.value)}
          min={blockStart || new Date().toISOString().split('T')[0]} />
        <label style={styles.fieldLabel}>Reason (optional)</label>
        <input style={styles.input} type="text"
          placeholder="e.g. Vacation, Personal, Medical..."
          value={blockReason} onChange={e => setBlockReason(e.target.value)} />
        <button style={styles.addBtn} onClick={addBlockedRange} disabled={!blockStart || !blockEnd}>
          + Block These Dates
        </button>
      </div>

      {Object.keys(groupedRanges).length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>🚫 Blocked Date Ranges</h3>
          {Object.entries(groupedRanges).map(([reason, dates]) => {
            const sorted = [...dates].sort();
            return (
              <div key={reason} style={styles.rangeItem}>
                <div style={styles.rangeInfo}>
                  <span style={styles.rangeReason}>{reason}</span>
                  <span style={styles.rangeDates}>
                    {sorted[0]}{sorted.length > 1 ? ` → ${sorted[sorted.length - 1]} (${sorted.length} days)` : ''}
                  </span>
                </div>
                <button style={styles.removeBtn} onClick={() => removeBlockedRange(reason)}>✕</button>
              </div>
            );
          })}
        </div>
      )}

      <button style={saving ? styles.saveBtnDisabled : styles.saveBtn}
        onClick={handleSave} disabled={saving}>
        {saving ? 'Saving...' : 'Save Availability'}
      </button>
    </div>
  );

  return (
    <div style={styles.container}>
      <div style={styles.navbar}>
        <h1 style={{ ...styles.logo, cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>TradePower</h1>
        <button style={styles.backButton} onClick={() => navigate('/dashboard')}>← Back</button>
      </div>

      <div style={styles.content}>
        <h2 style={styles.title}>My Availability</h2>
        <p style={styles.subtitle}>
          Block out dates and days you're not available. Contractors won't see you in search results on these dates.
        </p>

        {saved && <div style={styles.successBanner}>✅ Availability saved successfully!</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Calendar always on top on mobile, side by side on desktop */}
          {isMobile ? (
            <>
              {calendarSection}
              {controlsSection}
            </>
          ) : (
            <div style={styles.layout}>
              {controlsSection}
              {calendarSection}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5' },
  loadingContainer: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  navbar: {
    backgroundColor: 'white', padding: '16px 24px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  logo: { fontSize: '24px', fontWeight: 'bold', color: '#E94560', margin: 0 },
  backButton: {
    padding: '8px 16px', backgroundColor: 'transparent',
    color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer',
  },
  content: { maxWidth: '1100px', margin: '0 auto', padding: '24px 16px' },
  title: { fontSize: '26px', color: '#1a1a2e', marginBottom: '8px' },
  subtitle: { color: '#666', marginBottom: '20px', maxWidth: '600px', fontSize: '14px' },
  successBanner: {
    backgroundColor: '#d4edda', color: '#155724', padding: '12px 16px',
    borderRadius: '8px', marginBottom: '20px', textAlign: 'center',
  },
  layout: { display: 'grid', gridTemplateColumns: '360px 1fr', gap: '24px', alignItems: 'start' },
  controls: { display: 'flex', flexDirection: 'column', gap: '16px' },
  section: {
    backgroundColor: 'white', padding: '20px', borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  sectionTitle: { fontSize: '15px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '8px' },
  hint: { fontSize: '13px', color: '#999', marginBottom: '12px' },
  dayGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  dayBtn: {
    padding: '8px 12px', backgroundColor: 'white', color: '#666',
    border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '13px',
    touchAction: 'manipulation',
  },
  dayBtnBlocked: {
    padding: '8px 12px', backgroundColor: '#E94560', color: 'white',
    border: '1px solid #E94560', borderRadius: '8px', cursor: 'pointer', fontSize: '13px',
    touchAction: 'manipulation',
  },
  blockedSummary: { fontSize: '12px', color: '#E94560', marginTop: '10px', fontWeight: 'bold' },
  fieldLabel: { display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#555', marginBottom: '4px' },
  input: {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1px solid #ddd', fontSize: '14px',
    boxSizing: 'border-box', marginBottom: '12px',
  },
  addBtn: {
    width: '100%', padding: '10px', backgroundColor: '#1a1a2e', color: 'white',
    border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold',
    touchAction: 'manipulation',
  },
  rangeItem: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 12px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '8px',
  },
  rangeInfo: { display: 'flex', flexDirection: 'column', gap: '2px' },
  rangeReason: { fontSize: '13px', fontWeight: 'bold', color: '#1a1a2e' },
  rangeDates: { fontSize: '12px', color: '#999' },
  removeBtn: {
    width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#f0f0f0',
    border: 'none', cursor: 'pointer', fontSize: '12px', color: '#666',
  },
  saveBtn: {
    padding: '14px', backgroundColor: '#E94560', color: 'white',
    border: 'none', borderRadius: '8px', fontSize: '15px',
    fontWeight: 'bold', cursor: 'pointer', touchAction: 'manipulation',
  },
  saveBtnDisabled: {
    padding: '14px', backgroundColor: '#ccc', color: 'white',
    border: 'none', borderRadius: '8px', fontSize: '15px',
    fontWeight: 'bold', cursor: 'not-allowed',
  },
  calendarCard: {
    backgroundColor: 'white', borderRadius: '12px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '20px',
  },
  calendarHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: '16px',
  },
  navBtn: {
    padding: '8px 16px', backgroundColor: 'transparent',
    border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '16px',
    touchAction: 'manipulation',
  },
  monthTitle: { fontSize: '18px', fontWeight: 'bold', color: '#1a1a2e' },
  dayNamesRow: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '6px' },
  dayName: { textAlign: 'center', fontSize: '12px', fontWeight: 'bold', color: '#999', padding: '6px 0' },
  daysGrid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px' },
  emptyCell: { height: '48px' },
  dayCell: {
    height: '48px', borderRadius: '8px', display: 'flex',
    flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: '2px', border: '1px solid transparent',
  },
  todayCell: { backgroundColor: '#1a1a2e', color: 'white', fontWeight: 'bold' },
  blockedDayCell: { backgroundColor: '#ffebee', border: '1px solid #ffcdd2' },
  blockedDateCell: { backgroundColor: '#fce4ec', border: '1px solid #f48fb1' },
  dayNumber: { fontSize: '13px', fontWeight: '500' },
  blockedDot: { fontSize: '10px', color: '#E94560', fontWeight: 'bold' },
  legend: { display: 'flex', gap: '14px', marginTop: '14px', justifyContent: 'center', flexWrap: 'wrap' },
  legendItem: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#666' },
  legendBox: { width: '14px', height: '14px', borderRadius: '3px' },
};

export default Availability;